/**
 * SalvationAdminService
 * Console surface of Salvation Management ("Salvation Team", /app/salvation): overview, people, follow-ups, first
 * assignment and corrections, reopen / close / review, auto-allocation, CSV export, campus teams and members, settings.
 * Campus leaders see and change only the teams they lead (403 not_your_campus).
 * Route: salvation/api/salvation/admin/*
 */
import { BaseService } from '../base/BaseService';
import { unwrapSalvationResponse as unwrap, salvationListOrEmpty as listOrEmpty } from './unwrapSalvationResponse';
/** Console admin (api/salvation/admin). */
export class SalvationAdminService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? SalvationAdminService.DEFAULT_SERVICE_PATH,
            basePath: SalvationAdminService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    adminUrl(endpoint, params) {
        return this.buildUrlWithParams(`admin/${endpoint}`, params);
    }
    static peopleParams(query = {}) {
        return {
            teamId: query.teamId, statusId: query.statusId, stage: query.stage, flag: query.flag, search: query.search,
            sort: query.sort, page: query.page, pageSize: query.pageSize,
        };
    }
    // ---------------------------------------------------------------- overview + people
    /** GET admin/overview: the five counters, progress, per campus and priorities. */
    async getOverview(teamId) {
        return unwrap(await this.getOverviewRaw(teamId), 'Overview not found');
    }
    getOverviewRaw(teamId) {
        return this.http.get(this.adminUrl('overview', { teamId }));
    }
    /** GET admin/people: the filtered, paged list. */
    async getPeople(query = {}) {
        return unwrap(await this.getPeopleRaw(query), 'People not found');
    }
    getPeopleRaw(query = {}) {
        return this.http.get(this.adminUrl('people', SalvationAdminService.peopleParams(query)));
    }
    /** GET admin/people/export (Salvation.Export, audited): the filtered list as raw text/csv. */
    exportPeopleCsv(query = {}) {
        return this.http.getBlob(this.adminUrl('people/export', SalvationAdminService.peopleParams(query)));
    }
    async getPerson(convertId) {
        return unwrap(await this.getPersonRaw(convertId), 'Person not found');
    }
    getPersonRaw(convertId) {
        return this.http.get(this.adminUrl(`people/${convertId}`));
    }
    /** POST admin/people (teamID required): 409 duplicate_convert ("convertId:N" in errors[1]) unless confirmNotDuplicate. */
    async addPerson(request) {
        return unwrap(await this.addPersonRaw(request), 'The person could not be added');
    }
    addPersonRaw(request) {
        return this.http.post(this.adminUrl('people'), request);
    }
    /** PUT admin/people/{id}: correct details (409 stale when expectedRowVersion no longer matches). */
    async updatePerson(convertId, request) {
        return unwrap(await this.updatePersonRaw(convertId, request), 'The person could not be saved');
    }
    updatePersonRaw(convertId, request) {
        return this.http.put(this.adminUrl(`people/${convertId}`), request);
    }
    /** POST admin/people/{id}/follow-ups: logged as the signed-in leader. */
    async logFollowUp(convertId, request) {
        return unwrap(await this.logFollowUpRaw(convertId, request), 'The follow-up could not be saved');
    }
    logFollowUpRaw(convertId, request) {
        return this.http.post(this.adminUrl(`people/${convertId}/follow-ups`), request);
    }
    /** POST admin/people/{id}/assign: the first Lead / Backup (409 assignment_permanent once both are set). */
    async assign(convertId, request) {
        return unwrap(await this.assignRaw(convertId, request), 'The assignment could not be saved');
    }
    assignRaw(convertId, request) {
        return this.http.post(this.adminUrl(`people/${convertId}/assign`), request);
    }
    /** POST admin/people/{id}/correct-assignment: change a permanent pair with a reason (Salvation.Assignments.Correct). */
    async correctAssignment(convertId, request) {
        return unwrap(await this.correctAssignmentRaw(convertId, request), 'The assignment could not be corrected');
    }
    correctAssignmentRaw(convertId, request) {
        return this.http.post(this.adminUrl(`people/${convertId}/correct-assignment`), request);
    }
    async reopen(convertId, request) {
        return unwrap(await this.reopenRaw(convertId, request), 'The person could not be reopened');
    }
    reopenRaw(convertId, request) {
        return this.http.post(this.adminUrl(`people/${convertId}/reopen`), request);
    }
    async close(convertId, request) {
        return unwrap(await this.closeRaw(convertId, request), 'The person could not be closed');
    }
    closeRaw(convertId, request) {
        return this.http.post(this.adminUrl(`people/${convertId}/close`), request);
    }
    async reviewDone(convertId, request = {}) {
        return unwrap(await this.reviewDoneRaw(convertId, request), 'The review could not be saved');
    }
    reviewDoneRaw(convertId, request = {}) {
        return this.http.post(this.adminUrl(`people/${convertId}/review-done`), request);
    }
    /** POST admin/teams/{teamId}/allocate/preview: what auto-allocation would do (nothing is saved). */
    async previewAllocation(teamId) {
        return unwrap(await this.previewAllocationRaw(teamId), 'Allocation preview not available');
    }
    previewAllocationRaw(teamId) {
        return this.http.post(this.adminUrl(`teams/${teamId}/allocate/preview`), {});
    }
    /** POST admin/teams/{teamId}/allocate: gives everyone in follow-up without a Lead or Backup the least-loaded member. */
    async allocate(teamId) {
        return unwrap(await this.allocateRaw(teamId), 'Allocation failed');
    }
    allocateRaw(teamId) {
        return this.http.post(this.adminUrl(`teams/${teamId}/allocate`), {});
    }
    // ---------------------------------------------------------------- teams
    async getTeams() {
        return listOrEmpty(await this.getTeamsRaw());
    }
    getTeamsRaw() {
        return this.http.get(this.adminUrl('teams'));
    }
    /** GET admin/campuses: active campuses (Location Management) with the team already set up for each. */
    async getCampuses() {
        return listOrEmpty(await this.getCampusesRaw());
    }
    getCampusesRaw() {
        return this.http.get(this.adminUrl('campuses'));
    }
    /** POST admin/teams (admins only; one active team per campus). */
    async createTeam(request) {
        return unwrap(await this.createTeamRaw(request), 'The team could not be created');
    }
    createTeamRaw(request) {
        return this.http.post(this.adminUrl('teams'), request);
    }
    async updateTeam(teamId, request) {
        return unwrap(await this.updateTeamRaw(teamId, request), 'The team could not be saved');
    }
    updateTeamRaw(teamId, request) {
        return this.http.put(this.adminUrl(`teams/${teamId}`), request);
    }
    async getTeamMembers(teamId) {
        return listOrEmpty(await this.getTeamMembersRaw(teamId));
    }
    getTeamMembersRaw(teamId) {
        return this.http.get(this.adminUrl(`teams/${teamId}/members`));
    }
    /** POST admin/teams/{teamId}/members (userId required): accessGranted=false = the portal role still has to be given. */
    async addTeamMember(teamId, request) {
        return unwrap(await this.addTeamMemberRaw(teamId, request), 'The team member could not be added');
    }
    addTeamMemberRaw(teamId, request) {
        return this.http.post(this.adminUrl(`teams/${teamId}/members`), request);
    }
    async updateTeamMember(teamId, teamMemberId, request) {
        return unwrap(await this.updateTeamMemberRaw(teamId, teamMemberId, request), 'The team member could not be saved');
    }
    updateTeamMemberRaw(teamId, teamMemberId, request) {
        return this.http.put(this.adminUrl(`teams/${teamId}/members/${teamMemberId}`), request);
    }
    /** PUT admin/teams/{teamId}/members/order: the whole ranking (every member, first = rank 1). */
    async reorderTeamMembers(teamId, request) {
        return listOrEmpty(await this.reorderTeamMembersRaw(teamId, request));
    }
    reorderTeamMembersRaw(teamId, request) {
        return this.http.put(this.adminUrl(`teams/${teamId}/members/order`), request);
    }
    // ---------------------------------------------------------------- settings
    async getSettings() {
        return unwrap(await this.getSettingsRaw(), 'Settings not found');
    }
    getSettingsRaw() {
        return this.http.get(this.adminUrl('settings'));
    }
    /** PUT admin/settings (Salvation.Settings.Manage): partial update. */
    async saveSettings(request) {
        return unwrap(await this.saveSettingsRaw(request), 'Settings could not be saved');
    }
    saveSettingsRaw(request) {
        return this.http.put(this.adminUrl('settings'), request);
    }
}
SalvationAdminService.DEFAULT_SERVICE_PATH = 'salvation';
SalvationAdminService.BASE_PATH = 'api/salvation';
//# sourceMappingURL=SalvationAdminService.js.map